import { grey } from "@ant-design/colors"
import { faInbox, faRotate, faSpinnerThird } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { ConfigProvider, Empty, Flex, Input, Row, theme, Typography } from "antd"
import { useContext, useState } from "react"
import PageSidebar from "../common/PageSidebar"
import SidebarCreateButton from "../common/SidebarCreateButton"
import VSpace from "../common/VSpace"
import TenantsContext from "./TenantsContext"
import { emptyServerTenantImages, emptyTenantState } from "./tenantsModel"
import { useSidebarTenantsQuery } from "./tenantsQueries"
import TenantsSidebarList from "./TenantsSidebarList"

const { Text } = Typography

type Props = {
  onTenantSelect: (tenantId: number) => void
  publicBucketName: string
}

const TenantsSidebar = ({ onTenantSelect, publicBucketName }: Props) => {
  const { dispatch } = useContext(TenantsContext)
  const [searchTerm, setSearchTerm] = useState('')

  const useTenantsListForSidebarQuery = useSidebarTenantsQuery()
  const activeTenants = useTenantsListForSidebarQuery.data?.filter(tenant => tenant.active) ?? []
  const inactiveTenants = useTenantsListForSidebarQuery.data?.filter(tenant => !tenant.active) ?? []
  const filteredTenants = useTenantsListForSidebarQuery.data?.filter(tenant => tenant.name.toLowerCase().includes(searchTerm.toLowerCase()) || tenant.id.toString() === searchTerm)
  const isSearchActive = searchTerm.trim() !== ''

  return (
    <PageSidebar>
      <div>
        <VSpace size='large'>
          <VSpace style={{ padding: '10px 10px 0 10px' }}>
            <Row justify='space-between' align='middle'>
              <Text style={{ color: 'white', fontSize: 18, fontFamily: 'Open Sans, sans-serif' }}>
                Tenants
              </Text>
              <FontAwesomeIcon
                icon={faRotate}
                spin={useTenantsListForSidebarQuery.isLoading}
                speed={20}
                style={{ cursor: 'pointer', color: grey[2] }}
                onClick={() => useTenantsListForSidebarQuery.refetch()}
              />
            </Row>
            <Row>
              <Input.Search
                placeholder='Search'
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value ?? '')}
                allowClear
              />
            </Row>
          </VSpace>

          <SidebarCreateButton text='Create New Tenant' onClick={() => {
            onTenantSelect(0)
            dispatch({ type: 'RESET_FORM', payload: emptyTenantState(emptyServerTenantImages()) })
          }
          }
          />
        </VSpace>
      </div>

      <ConfigProvider
        theme={{
          algorithm: theme.darkAlgorithm,
        }}
        renderEmpty={() =>
          <Empty
            description='No items found'
            image={<FontAwesomeIcon icon={faInbox} size='xl' />}
          />
        }
      >
        {useTenantsListForSidebarQuery.isFetching ? (
          <Flex vertical align='center' style={{ paddingTop: '2rem' }}>
            <FontAwesomeIcon icon={faSpinnerThird} color={grey[3]} spin />
            <Text type='secondary'>Loading tenants</Text>
          </Flex>
        ) : (
          <div className='react-scrollable-list'
            style={{ height: `calc(100vh - 170px )` }}
          >
            {!isSearchActive ? <>
              <Text style={{ padding: '10px 10px', fontWeight: 'bold' }}>Active Tenants</Text>
              <TenantsSidebarList listData={activeTenants} onTenantSelect={onTenantSelect} publicBucketName={publicBucketName} />
              <Text style={{ padding: '10px 10px', fontWeight: 'bold' }}>Inactive Tenants</Text>
              <TenantsSidebarList listData={inactiveTenants} onTenantSelect={onTenantSelect} publicBucketName={publicBucketName} />
            </>
              : <TenantsSidebarList listData={filteredTenants ?? []} onTenantSelect={onTenantSelect} publicBucketName={publicBucketName} />
            }
          </div>
        )}
      </ConfigProvider>
    </PageSidebar>
  )
}

export default TenantsSidebar